import { ApiClient } from '@/api/apiClient';
import { LabelField, LazyImage } from '@/components';
import { formatDateWithDayOfWeek, formatPricing, formatTime } from '@/utils';
import {
  Box,
  Button,
  DetailsDrawer,
  Dialog,
  Link,
  Typography,
  useTheme,
} from '@silverstein-properties/inspirelabs-ui';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { HostList } from '../SchedulingModule/components';
import { CircularProgress, SxProps } from '@mui/material';
import { useState } from 'react';
import {
  MessagingDialog,
  MessagingDialogProps,
} from '@/components/MessagingDialog/MessagingDialog';
import { useAuth, useImageDownloader } from '@/hooks';

export const BookingDetailsDrawer = () => {
  const apiClient = ApiClient();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const bookingId = searchParams.get('bookingId');
  const isOpen = !!bookingId;
  const { data: user } = useAuth();

  const [messageBoxInfo, setMessageBoxInfo] = useState<
    MessagingDialogProps | undefined
  >();

  const handleClose = () => {
    setSearchParams({ bookingId: '' });
  };

  const { data: booking, isLoading } = useQuery({
    enabled: !!bookingId,
    queryKey: ['booking', bookingId],
    queryFn: () =>
      apiClient.bookings.getBookingById({ bookingId: bookingId || '' }),
  });

  const { downloadImages, isLoading: isDownloading } = useImageDownloader();

  const listing = booking?.listingSnapshot;
  const customerPrice = booking?.pricing?.total || 0;

  const locationDetails: { value: string; style?: SxProps; hide?: boolean }[] =
    [
      {
        value: `${booking?.consumer?.firstName} ${booking?.consumer?.lastName}`,
      },
      {
        value: booking?.location?.additionalDetail?.tenantName || '',
        style: { mt: theme.spacers.s },
      },
      {
        value: booking?.location?.additionalDetail?.buildingName || '',
      },
      { value: booking?.consumer?.phoneNumber || '' },
      {
        value: 'Floor:',
        style: { mt: theme.spacers.s },
        hide: !Boolean(booking?.location?.additionalDetail?.floor),
      },
      {
        value: booking?.location?.additionalDetail?.floor || '',
        style: { fontWeight: theme.typography.fontWeightMedium },
      },
      {
        value: 'Office/Area/Venue: ',
        style: { mt: theme.spacers.s },
        hide: !Boolean(
          booking?.location?.additionalDetail?.officeNumber ||
            booking?.location?.additionalDetail?.unit ||
            booking?.location?.address?.apartment ||
            booking?.location?.additionalDetail?.description
        ),
      },
      {
        value:
          booking?.location?.additionalDetail?.officeNumber ||
          booking?.location?.additionalDetail?.unit ||
          booking?.location?.address?.apartment ||
          booking?.location?.additionalDetail?.description ||
          '',
        style: { fontWeight: theme.typography.fontWeightMedium },
      },
    ];

  const handleContactCustomer = () => {
    setMessageBoxInfo({
      messageType: 'one',
      bookingId: booking?.consumerId || '',
      listingId: booking?.listingSnapshot.id || '',
      firstName: booking?.consumer?.firstName || '',
      lastName: booking?.consumer?.lastName || '',
    });
  };

  return (
    <DetailsDrawer
      onClose={handleClose}
      open={isOpen}
      title="Booking information"
    >
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : booking ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacers.l,
          }}
        >
          <Typography variant="h5" sx={{ mb: theme.spacers.m }}>
            {booking?.listingSnapshot.title}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            sx={{ width: 'fit-content' }}
            onClick={handleContactCustomer}
          >
            Contact Customer
          </Button>
          <LabelField
            label="Date"
            value={formatDateWithDayOfWeek(listing?.startsAt || '')}
          />
          <LabelField
            label="Time"
            value={`${formatTime(listing?.startsAt || '')} - ${formatTime(
              listing?.endsAt || ''
            )} `}
          />
          <LabelField
            label="Service provider"
            value={
              <HostList
                hosts={
                  listing?.host
                    ? [
                        {
                          ...listing?.host,
                          firstName:
                            listing?.host?.firstName || user?.firstName || '',
                        },
                      ]
                    : []
                }
              />
            }
          />
          <LabelField
            label="Amount paid by customer"
            value={
              customerPrice > 0 ? (
                <Typography>{formatPricing(customerPrice)}</Typography>
              ) : (
                <Typography sx={{ color: theme.palette.success.main }}>
                  FREE
                </Typography>
              )
            }
          />
          <LabelField
            label="Location"
            value={
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {locationDetails.map((item, index) =>
                  item.hide ? null : (
                    <Typography key={index} sx={item.style}>
                      {item.value}
                    </Typography>
                  )
                )}
              </Box>
            }
          />
          {(booking?.specialInstructions ||
            (booking?.instructionsFiles || []).length > 0) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacers.xxl,
                mb: theme.spacers.m,
              }}
            >
              {booking?.specialInstructions && (
                <LabelField
                  label="Special instructions"
                  value={booking?.specialInstructions || ''}
                />
              )}
              {booking?.instructionsFiles?.map((file, index) => (
                <LazyImage key={index} src={file} alt="special instructions" />
              ))}
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacers.m,
            }}
          >
            {(booking?.instructionsFiles || []).length > 0 && (
              <Link
                color="primary"
                variant="body2"
                onClick={() =>
                  !isDownloading &&
                  downloadImages(booking?.instructionsFiles || [])
                }
                sx={{ cursor: isDownloading ? 'not-allowed' : 'pointer' }}
              >
                Download images
              </Link>
            )}
            {/* TODO: Add customer details link when we have the customer details page */}
            {/* <Link color="primary" variant="body2">
              View customer details
            </Link> */}
          </Box>
        </Box>
      ) : (
        <Box>
          <Typography>
            Booking details could not be retrieved. Please try again later.
          </Typography>
        </Box>
      )}
      {!!messageBoxInfo && (
        <Dialog
          open={!!messageBoxInfo}
          onClose={() => setMessageBoxInfo(undefined)}
        >
          <MessagingDialog {...messageBoxInfo} />
        </Dialog>
      )}
    </DetailsDrawer>
  );
};
